import api from '@api/index'
import { Color } from '@enums/Colors'
import { DownloadDTO } from './DownloadDTO'
import dayjs, { Dayjs } from 'dayjs'
import { DownloadRequestParams } from './DownloadRequestParams'
import { DownloadState } from './DownloadState'
import { DownloadType } from './DownloadType'
import { FileType } from '@enums/FileType'
import { MediaDownloadRequestParams } from '@models/api/downloads/MediaDownloadRequestParams'
import { DownloadHelper } from '@helpers/DownloadHelper'
import { DescargaVariablePDFRequestParamsDTO } from '@models/api/DescargaVariablePDFRequestParamsDTO'
import { ExtremaduraBulletinRequestParamsDTO } from '@models/api/ExtremaduraBulletinRequestParamsDTO'
import { BulletinDownloadRequestParams } from '@models/api/BulletinDownloadRequestParams'

export class Download {
  id: string
  title: string
  type: DownloadType
  fileType: FileType
  state: DownloadState
  progress?: number
  request?: DownloadRequestParams
  creationDate?: Dayjs
  shouldPrintWhenCompleted?: boolean

  private constructor(
    id: string,
    title: string,
    type: DownloadType,
    fileType: FileType,
    state: DownloadState,
    request?: DownloadRequestParams,
    progress?: number,
    shouldPrintWhenCompleted?: boolean
  ) {
    this.id = id
    this.title = title
    this.type = type
    this.fileType = fileType
    this.state = state
    this.progress = progress
    this.request = request
    this.shouldPrintWhenCompleted = shouldPrintWhenCompleted
  }

  getStateTranslationKey() {
    switch (this.state) {
      case 'pending':
        return 'pending'
      case 'error':
        return 'error'
      case 'generating':
        return 'generating'
      case 'complete':
        return 'completed'
    }
  }

  getTitleWithExtension() {
    return `${this.title}.${FileType.getExtension(this.fileType)}`
  }

  getStateColor() {
    switch (this.state) {
      case 'pending':
        return Color.VEXIZA_GRAY
      case 'error':
        return Color.VEXIZA_RED
      case 'generating':
        return Color.VEXIZA_BLUE
      case 'complete':
        return Color.VEXIZA_GREEN
    }
  }

  downloadFile(isOnlyDownload?: boolean) {
    const downloadAction: Record<DownloadType, () => void> = {
      [DownloadType.MEDIA]: () => {
        api.media.getMedia(
          this.request as MediaDownloadRequestParams,
          this.title
        )
      },
      [DownloadType.BULLETIN_EXTREMADURA]: () => {
        api.bulletinConfiguration.downloadBulletinExtremadura(
          this.title,
          FileType.getMimeType(this.fileType),
          FileType.getExtension(this.fileType)
        )
      },
      [DownloadType.PDF]: () => {
        DownloadHelper.handleDownload(
          api.descargaVariablePDF.get(
            this.request as DescargaVariablePDFRequestParamsDTO
          ),
          this.title,
          this.fileType,
          this.type,
          this.request as DownloadRequestParams
        )
      },
      [DownloadType.BULLETIN_WILDFIRE_EXTREMADURA]: () => {
        DownloadHelper.handleDownload(
          api.meteogram.getReportMeteograma(
            this.request as ExtremaduraBulletinRequestParamsDTO
          ),
          this.title,
          this.fileType,
          this.type
        )
      },
      [DownloadType.BULLETIN_PDF]: () => {
        const { bulletinID, downloadID } = this
          .request as BulletinDownloadRequestParams

        DownloadHelper.handleDownload(
          api.bulletinConfiguration.downloadBulletinPDF(bulletinID, downloadID),
          this.title,
          this.fileType,
          this.type,
          undefined,
          !isOnlyDownload ? this.shouldPrintWhenCompleted : undefined
        )
      }
    }
    downloadAction[this.type]()
  }

  static createPending(
    id: string,
    title: string,
    type: DownloadType,
    fileType: FileType,
    request?: DownloadRequestParams,
    shouldPrintWhenCompleted?: boolean
  ) {
    return new Download(
      id,
      title,
      type,
      fileType,
      'pending',
      request,
      undefined,
      shouldPrintWhenCompleted
    )
  }

  toDTO(): DownloadDTO {
    return {
      id: this.id,
      title: this.title,
      type: this.type,
      fileType: this.fileType,
      state: this.state,
      progress: this.progress,
      request: this.request,
      creationDate: this.creationDate?.toISOString()
    }
  }

  static fromDTO(dto: DownloadDTO) {
    const download = new Download(
      dto.id,
      dto.title,
      dto.type,
      dto.fileType,
      dto.state,
      dto.request,
      dto.progress
    )
    if (dto.creationDate) {
      download.creationDate = dayjs(dto.creationDate)
    }
    return download
  }
}
